import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as drinkingGameService from '@/services/drinkingGame.service';
import type {
  DrinkingGame,
  DrinkingGameSession,
  DrinkingGameAction,
  SessionStats,
  DrinkingGameType,
  CreateDrinkingGameInput,
  UpdateDrinkingGameInput,
  GameActionInput,
} from '@/services/drinkingGame.service';
import { ApiError } from '@/services/api';

// State interface
interface DrinkingGameState {
  // Games
  games: DrinkingGame[];
  currentGame: DrinkingGame | null;

  // Sessions
  sessions: DrinkingGameSession[];
  currentSession: DrinkingGameSession | null;
  sessionStats: SessionStats | null;

  // Actions history for current session
  sessionActions: DrinkingGameAction[];

  // Filters
  filters: {
    gameType?: DrinkingGameType;
    activeOnly: boolean;
  };

  // Loading states
  isLoadingGames: boolean;
  isLoadingGame: boolean;
  isLoadingSessions: boolean;
  isLoadingSession: boolean;
  isLoadingStats: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isJoining: boolean;
  isStarting: boolean;
  isSubmittingAction: boolean;

  // Error
  error: string | null;
}

// Initial state
const initialState: DrinkingGameState = {
  games: [],
  currentGame: null,
  sessions: [],
  currentSession: null,
  sessionStats: null,
  sessionActions: [],
  filters: {
    activeOnly: false,
  },
  isLoadingGames: false,
  isLoadingGame: false,
  isLoadingSessions: false,
  isLoadingSession: false,
  isLoadingStats: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isJoining: false,
  isStarting: false,
  isSubmittingAction: false,
  error: null,
};

// ============ Game Thunks ============

export const fetchGames = createAsyncThunk<
  DrinkingGame[],
  { gameType?: DrinkingGameType; activeOnly?: boolean } | undefined,
  { rejectValue: string }
>('drinkingGames/fetchGames', async (filters, { rejectWithValue, getState }) => {
  try {
    const state = getState() as { drinkingGames: DrinkingGameState };
    const gameType = filters?.gameType ?? state.drinkingGames.filters.gameType;
    const activeOnly = filters?.activeOnly ?? state.drinkingGames.filters.activeOnly;

    const result = await drinkingGameService.getGames(gameType, activeOnly);
    return result;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Spiele');
  }
});

export const fetchGameById = createAsyncThunk<
  DrinkingGame,
  string,
  { rejectValue: string }
>('drinkingGames/fetchGameById', async (id, { rejectWithValue }) => {
  try {
    return await drinkingGameService.getGameById(id);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden des Spiels');
  }
});

export const createGame = createAsyncThunk<
  DrinkingGame,
  CreateDrinkingGameInput,
  { rejectValue: string }
>('drinkingGames/createGame', async (data, { rejectWithValue }) => {
  try {
    return await drinkingGameService.createGame(data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Erstellen des Spiels');
  }
});

export const updateGame = createAsyncThunk<
  DrinkingGame,
  { id: string; data: UpdateDrinkingGameInput },
  { rejectValue: string }
>('drinkingGames/updateGame', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await drinkingGameService.updateGame(id, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Aktualisieren des Spiels');
  }
});

export const deleteGame = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('drinkingGames/deleteGame', async (id, { rejectWithValue }) => {
  try {
    await drinkingGameService.deleteGame(id);
    return id;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Loeschen des Spiels');
  }
});

// ============ Session Thunks ============

export const fetchActiveSessions = createAsyncThunk<
  DrinkingGameSession[],
  void,
  { rejectValue: string }
>('drinkingGames/fetchActiveSessions', async (_, { rejectWithValue }) => {
  try {
    return await drinkingGameService.getActiveSessions();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Sessions');
  }
});

export const fetchSession = createAsyncThunk<
  DrinkingGameSession,
  string,
  { rejectValue: string }
>('drinkingGames/fetchSession', async (id, { rejectWithValue }) => {
  try {
    return await drinkingGameService.getSession(id);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Session');
  }
});

export const createSession = createAsyncThunk<
  DrinkingGameSession,
  string,
  { rejectValue: string }
>('drinkingGames/createSession', async (gameId, { rejectWithValue }) => {
  try {
    return await drinkingGameService.createSession(gameId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Erstellen der Session');
  }
});

export const joinSession = createAsyncThunk<
  DrinkingGameSession,
  string,
  { rejectValue: string }
>('drinkingGames/joinSession', async (sessionId, { rejectWithValue }) => {
  try {
    return await drinkingGameService.joinSession(sessionId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Beitreten der Session');
  }
});

export const leaveSession = createAsyncThunk<
  DrinkingGameSession,
  string,
  { rejectValue: string }
>('drinkingGames/leaveSession', async (sessionId, { rejectWithValue }) => {
  try {
    return await drinkingGameService.leaveSession(sessionId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Verlassen der Session');
  }
});

export const startSession = createAsyncThunk<
  DrinkingGameSession,
  string,
  { rejectValue: string }
>('drinkingGames/startSession', async (sessionId, { rejectWithValue }) => {
  try {
    return await drinkingGameService.startSession(sessionId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Starten des Spiels');
  }
});

export const endSession = createAsyncThunk<
  DrinkingGameSession,
  string,
  { rejectValue: string }
>('drinkingGames/endSession', async (sessionId, { rejectWithValue }) => {
  try {
    return await drinkingGameService.endSession(sessionId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Beenden des Spiels');
  }
});

export const submitAction = createAsyncThunk<
  DrinkingGameAction,
  { sessionId: string; data: GameActionInput },
  { rejectValue: string }
>('drinkingGames/submitAction', async ({ sessionId, data }, { rejectWithValue }) => {
  try {
    return await drinkingGameService.submitAction(sessionId, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Ausfuehren der Aktion');
  }
});

export const fetchSessionStats = createAsyncThunk<
  SessionStats,
  string,
  { rejectValue: string }
>('drinkingGames/fetchSessionStats', async (sessionId, { rejectWithValue }) => {
  try {
    return await drinkingGameService.getSessionStats(sessionId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Statistiken');
  }
});

// ============ Slice ============

const drinkingGameSlice = createSlice({
  name: 'drinkingGames',
  initialState,
  reducers: {
    setFilters: (
      state,
      action: PayloadAction<{ gameType?: DrinkingGameType; activeOnly?: boolean }>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentGame: (state) => {
      state.currentGame = null;
    },
    clearCurrentSession: (state) => {
      state.currentSession = null;
      state.sessionStats = null;
      state.sessionActions = [];
    },
    addSessionAction: (state, action: PayloadAction<DrinkingGameAction>) => {
      state.sessionActions.push(action.payload);
    },
    updateSessionFromRealtime: (state, action: PayloadAction<DrinkingGameSession>) => {
      state.currentSession = action.payload;
      // Update in sessions list too
      const index = state.sessions.findIndex((s) => s.id === action.payload.id);
      if (index !== -1) {
        state.sessions[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch games
    builder
      .addCase(fetchGames.pending, (state) => {
        state.isLoadingGames = true;
        state.error = null;
      })
      .addCase(fetchGames.fulfilled, (state, action) => {
        state.isLoadingGames = false;
        state.games = action.payload;
      })
      .addCase(fetchGames.rejected, (state, action) => {
        state.isLoadingGames = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Fetch game by ID
    builder
      .addCase(fetchGameById.pending, (state) => {
        state.isLoadingGame = true;
        state.error = null;
      })
      .addCase(fetchGameById.fulfilled, (state, action) => {
        state.isLoadingGame = false;
        state.currentGame = action.payload;
      })
      .addCase(fetchGameById.rejected, (state, action) => {
        state.isLoadingGame = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Create game
    builder
      .addCase(createGame.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createGame.fulfilled, (state, action) => {
        state.isCreating = false;
        state.games.unshift(action.payload);
        state.currentGame = action.payload;
      })
      .addCase(createGame.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Update game
    builder
      .addCase(updateGame.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateGame.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.games.findIndex((g) => g.id === action.payload.id);
        if (index !== -1) {
          state.games[index] = action.payload;
        }
        if (state.currentGame?.id === action.payload.id) {
          state.currentGame = action.payload;
        }
      })
      .addCase(updateGame.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Delete game
    builder
      .addCase(deleteGame.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteGame.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.games = state.games.filter((g) => g.id !== action.payload);
        if (state.currentGame?.id === action.payload) {
          state.currentGame = null;
        }
      })
      .addCase(deleteGame.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Fetch active sessions
    builder
      .addCase(fetchActiveSessions.pending, (state) => {
        state.isLoadingSessions = true;
        state.error = null;
      })
      .addCase(fetchActiveSessions.fulfilled, (state, action) => {
        state.isLoadingSessions = false;
        state.sessions = action.payload;
      })
      .addCase(fetchActiveSessions.rejected, (state, action) => {
        state.isLoadingSessions = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Fetch session
    builder
      .addCase(fetchSession.pending, (state) => {
        state.isLoadingSession = true;
        state.error = null;
      })
      .addCase(fetchSession.fulfilled, (state, action) => {
        state.isLoadingSession = false;
        state.currentSession = action.payload;
      })
      .addCase(fetchSession.rejected, (state, action) => {
        state.isLoadingSession = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Create session
    builder
      .addCase(createSession.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createSession.fulfilled, (state, action) => {
        state.isCreating = false;
        state.sessions.unshift(action.payload);
        state.currentSession = action.payload;
      })
      .addCase(createSession.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Join session
    builder
      .addCase(joinSession.pending, (state) => {
        state.isJoining = true;
        state.error = null;
      })
      .addCase(joinSession.fulfilled, (state, action) => {
        state.isJoining = false;
        state.currentSession = action.payload;
        const index = state.sessions.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.sessions[index] = action.payload;
        }
      })
      .addCase(joinSession.rejected, (state, action) => {
        state.isJoining = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Leave session
    builder
      .addCase(leaveSession.pending, (state) => {
        state.isJoining = true;
        state.error = null;
      })
      .addCase(leaveSession.fulfilled, (state, action) => {
        state.isJoining = false;
        state.currentSession = null;
        const index = state.sessions.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.sessions[index] = action.payload;
        }
      })
      .addCase(leaveSession.rejected, (state, action) => {
        state.isJoining = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Start session
    builder
      .addCase(startSession.pending, (state) => {
        state.isStarting = true;
        state.error = null;
      })
      .addCase(startSession.fulfilled, (state, action) => {
        state.isStarting = false;
        state.currentSession = action.payload;
        const index = state.sessions.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.sessions[index] = action.payload;
        }
      })
      .addCase(startSession.rejected, (state, action) => {
        state.isStarting = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // End session
    builder
      .addCase(endSession.pending, (state) => {
        state.isStarting = true;
        state.error = null;
      })
      .addCase(endSession.fulfilled, (state, action) => {
        state.isStarting = false;
        state.currentSession = action.payload;
        // Remove from active sessions list
        state.sessions = state.sessions.filter((s) => s.id !== action.payload.id);
      })
      .addCase(endSession.rejected, (state, action) => {
        state.isStarting = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Submit action
    builder
      .addCase(submitAction.pending, (state) => {
        state.isSubmittingAction = true;
        state.error = null;
      })
      .addCase(submitAction.fulfilled, (state, action) => {
        state.isSubmittingAction = false;
        state.sessionActions.push(action.payload);
      })
      .addCase(submitAction.rejected, (state, action) => {
        state.isSubmittingAction = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Fetch session stats
    builder
      .addCase(fetchSessionStats.pending, (state) => {
        state.isLoadingStats = true;
        state.error = null;
      })
      .addCase(fetchSessionStats.fulfilled, (state, action) => {
        state.isLoadingStats = false;
        state.sessionStats = action.payload;
      })
      .addCase(fetchSessionStats.rejected, (state, action) => {
        state.isLoadingStats = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });
  },
});

export const {
  setFilters,
  clearError,
  clearCurrentGame,
  clearCurrentSession,
  addSessionAction,
  updateSessionFromRealtime,
} = drinkingGameSlice.actions;

export default drinkingGameSlice.reducer;
